import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, Route, Routes, useMatch } from "react-router-dom";

import { dealsApi } from "@/api/endpoints";
import { useAuth } from "@/auth/use-auth";
import { Topbar } from "@/components/layout/Topbar";
import { useI18n } from "@/i18n/use-i18n";
import { DashboardPage } from "@/pages/DashboardPage";
import { DealPage } from "@/pages/DealPage";
import { LoginPage } from "@/pages/LoginPage";
import { NewDealPage } from "@/pages/NewDealPage";
import { SetPasswordPage } from "@/pages/SetPasswordPage";

export function AppRoutes() {
  const { user, loading } = useAuth();
  const { t } = useI18n();

  // Rendering the login screen while the stored token is still being verified
  // would flash it on every reload for an already-signed-in user.
  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center bg-page text-muted">
        {t("app.loading")}
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/passwort-setzen" element={<SetPasswordPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Shell />}>
        <Route index element={<DashboardPage />} />
        <Route path="vorgang" element={<NewDealPage />} />
        <Route path="vorgang/:dealId" element={<DealPage />} />
      </Route>
      {/* Reachable while signed in too: someone who requested a reset and then
          logged in on another tab must still be able to use their link. */}
      <Route path="/passwort-setzen" element={<SetPasswordPage />} />
      {/* A signed-in user has no use for the login route. */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** App chrome: topbar plus the routed page. */
function Shell() {
  const { t } = useI18n();

  // useParams does not expose child-route params to a parent element, so the
  // deal id is read by matching the path directly.
  const match = useMatch("/vorgang/:dealId");
  const dealId = match?.params.dealId;

  // Shares the cache entry DealPage populates, and unlike getQueryData this
  // re-renders the topbar when the deal is loaded or updated.
  const deal = useQuery({
    queryKey: ["deal", dealId],
    queryFn: () => dealsApi.detail(dealId!),
    enabled: Boolean(dealId),
  });

  return (
    <div className="min-h-dvh">
      <Topbar dealReference={deal.data?.reference} />
      <Outlet />
      <footer className="pb-8 text-center font-mono text-[11.5px] text-muted">
        {t("app.footerBrand")} · {new Date().getFullYear()}
      </footer>
    </div>
  );
}
